import React, { useContext, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import SendIcon from '@mui/icons-material/Send'
import {
  Alert,
  Button,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TextField,
} from '@mui/material'
import { UserContext } from '../../App'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import AmbuNetLogo from '../../assets/images/ambunetLogo.png'
import { DetailRow } from '../Cards/DetailsBox'
import {
  ArrowRightAlt,
  CheckBox,
  CheckBoxOutlineBlank,
  FilterAlt,
} from '@mui/icons-material'
import API from '../../services/API'

const formatLogValue = (value) => {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) {
    if (value.length === 0) return '—'
    return value.map(formatLogValue).join(', ')
  }
  if (typeof value === 'object') {
    return Object.entries(value)
      .filter(([key]) => !['_id', '__v'].includes(key))
      .map(([key, v]) => `${key}: ${formatLogValue(v)}`)
      .join(', ')
  }
  return String(value)
}

const SingleLog = ({ log, handleDeleteComment }) => {
  const { user } = useContext(UserContext)

  const [anchorEl, setAnchorEl] = useState(null)
  const commentOwner = log.author?._id === user._id
  const isAdmin =
    user.employee?.teams.includes('Admin') || user?.security?.isAdmin
  if (log.user) {
    log.author = log.user
  }
  if (log.comment) {
    log.note = log.comment
  }

  return (
    <Box
      key={log._id}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start', // ✅ fixes the “note sits low” issue
        gap: 1.5,
        p: 1.5,
      }}
    >
      <Avatar
        src={
          log.systemAuthor || !log.author ? AmbuNetLogo : log.author?.photograph
        }
        sx={{ width: 28, height: 28, mt: '2px' }}
      />

      <Box sx={{ flex: 1, minWidth: 0, pr: 4 }}>
        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          {log.author && (
            <Typography fontWeight={700}>
              {log.author?.firstName} {log.author?.lastName}
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary">
            {moment(log.createdAt).format('Do MMM YYYY • HH:mm')}
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ mt: 0.75 }}>
          {log.note && (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {log.note}
            </Typography>
          )}

          {log.recordAccess && (
            <Typography variant="body2" color="text.secondary">
              Record accessed
            </Typography>
          )}

          {log?.changesMade && Object.keys(log.changesMade).length > 0 && (
            <Stack
              spacing={0.5}
              sx={{ mt: log.note || log.recordAccess ? 1 : 0 }}
              divider={<Divider />}
            >
              {Object.keys(log.changesMade).map((x) => {
                const { from, to } = log.changesMade[x]
                return (
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    key={x}
                    sx={{
                      // display: 'grid',
                      // gridTemplateColumns: '120px 1fr',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: 'text.secondary' }}
                    >
                      {x}
                    </Typography>

                    <Typography
                      variant="body2"
                      component="div"
                      // sx={{ overflowWrap: 'anywhere' }}
                    >
                      <Box
                        component="span"
                        sx={{
                          color: 'anRed.main',
                          textDecoration: 'line-through',
                        }}
                      >
                        {formatLogValue(from)}
                      </Box>{' '}
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          mx: 0.75,
                          lineHeight: 1,
                          verticalAlign: 'middle',
                          color: 'text.secondary',
                        }}
                      >
                        <ArrowRightAlt />
                      </Box>
                      <Box
                        component="span"
                        sx={{ fontWeight: 600, color: 'anGreen.main' }}
                      >
                        {formatLogValue(to)}
                      </Box>
                    </Typography>
                  </Stack>
                )
              })}
            </Stack>
          )}
        </Box>
      </Box>

      {/* Menu */}
      {handleDeleteComment &&
        ((commentOwner && log?.allowDelete) ||
          (isAdmin && !log?.systemComment)) && (
          <Box sx={{ position: 'absolute', top: 6, right: 6 }}>
            <IconButton
              size="small"
              sx={{ borderRadius: 2 }}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={!!anchorEl}
              onClose={() => setAnchorEl(null)}
            >
              <MenuItem onClick={() => handleDeleteComment(log._id)}>
                Delete
              </MenuItem>
            </Menu>
          </Box>
        )}
    </Box>
  )
}

const LogsComponent = ({
  logs = [],
  handleDeleteComment,
  onCreateLog,
  hideTitle,
  title,
  targetType,
  targetId,
  allowCreateLog,
  allowDelete,
  hideIfEmpty,
  openComment,
  setOpenComment,
  disableFiltering,
  hideDivider,
  logsLoadedAt, // Reload the loags due to the useEffect
  ...props
}) => {
  const [newCommentText, setNewCommentText] = useState('')
  const containerRef = useRef(null)
  const [viewOptions, setViewOptions] = useState([])
  const [selectedViews, setSelectedViews] = useState([])
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null)
  const [logsToDisplay, setLogsToDisplay] = useState([])
  const [authors, setAuthors] = useState({})
  const [fetchError, setFetchError] = useState(false)
  const [loading, setLoading] = useState(true)
  const { user } = useContext(UserContext)

  useEffect(() => {
    if (openComment && containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behaviour: 'smooth' })
    }
  }, [setOpenComment])

  // useEffect(() => {
  //   API.post('/fixlogs').then(res => res.status === 200 && console.log('tada'))
  // }, [])

  useEffect(() => {
    const combined = [...(logs || []), ...(logsToDisplay || [])]

    const hasNotes = combined.some((x) => x.note || x.comment)
    const hasChanges = combined.some(
      (x) => Object.keys(x.changesMade ?? {}).length > 0,
    )
    const hasAccess = combined.some((x) => x.recordAccess)

    setViewOptions((prev) => {
      const next = new Set(prev)
      if (hasNotes) next.add('Notes')
      if (hasChanges) next.add('Changes Made')
      if (hasAccess) next.add('Access Logs')
      return Array.from(next)
    })

    // Only auto-select defaults when they become available
    setSelectedViews((prev) => {
      const next = new Set(prev)
      if (hasNotes) next.add('Notes')
      if (hasChanges) next.add('Changes Made')
      // (maybe don’t auto-select Access Logs unless you want it)
      return Array.from(next)
    })
  }, [
    // use stable deps that won't change just because array identity changes
    logs?.length,
    logsToDisplay?.length,
  ])

  useEffect(() => {
    if (targetId && targetType) {
      API.get(`/logs?targetId=${targetId}&targetType=${targetType}`)
        .then((res) => {
          if (res.status === 200) {
            if (logsLoadedAt) {
              setLogsToDisplay((prev) => [...res.data.logs])
            } else {
              setLogsToDisplay((prev) => [...res.data.logs, ...prev])
            }
            setAuthors(res.data.authors)
          } else {
            setFetchError(true)
          }
        })
        .catch(() => setFetchError(true))
        .finally(() => setLoading(false))
    }
  }, [targetId, logsLoadedAt])

  return (
    <Stack gap={1}>
      <Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Typography variant="title2" fontWeight={600} gutterBottom>
            {hideTitle ? '' : title ? `${title}s` : 'Logs'}
          </Typography>
          <Box display="flex" spacing={1}>
            {viewOptions.length > 0 && !disableFiltering && (
              <Button
                onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                size="small"
                color="anBlue"
              >
                <FilterAlt />
              </Button>
            )}
            <Menu
              open={!!filterMenuAnchor}
              anchorEl={filterMenuAnchor}
              onClose={() => setFilterMenuAnchor(null)}
            >
              {viewOptions.map((x) => {
                const selected = selectedViews.includes(x)

                return (
                  <MenuItem
                    key={x}
                    onClick={() => {
                      setSelectedViews((prev) =>
                        selected ? prev.filter((y) => y !== x) : [...prev, x],
                      )
                    }}
                  >
                    <Box display="flex" gap={1} alignItems="center">
                      {selected ? (
                        <CheckBox color="anGreen" />
                      ) : (
                        <CheckBoxOutlineBlank />
                      )}
                      <Typography>{x}</Typography>
                    </Box>
                  </MenuItem>
                )
              })}
            </Menu>
            {(!!onCreateLog || (allowCreateLog && targetId && targetType)) && (
              <Button
                size="small"
                color="anBlue"
                onClick={() => setOpenComment((prev) => !prev)}
                variant="outlined"
                sx={{ mb: 2 }}
              >
                Add {title ?? 'Log'}
              </Button>
            )}
          </Box>
        </Stack>
        {!hideDivider && <Divider />}
      </Stack>
      <Stack
        {...props}
        divider={
          !hideDivider && [...logs, ...logsToDisplay]?.length && <Divider />
        }
        gap={0.4}
        ref={containerRef}
        maxHeight={400}
        overflow={props.overflow ?? 'scroll'}
      >
        {[true, null].includes(openComment) && (
          <Stack
            gap={1}
            alignItems="stretch"
            direction="row"
            component="form"
            onSubmit={(e) => {
              e.preventDefault()
              if (onCreateLog) {
                onCreateLog(newCommentText)
              } else if (allowCreateLog && targetId && targetType) {
                API.post('/logs', {
                  targetId,
                  targetType,
                  note: newCommentText,
                }).then((res) => {
                  if (res.status === 200) {
                    setLogsToDisplay((prev) => [
                      {
                        ...res.data,
                        author: {
                          firstName: user.firstName,
                          lastName: user.lastName,
                          photograph: user.photograph,
                        },
                      },
                      ...prev,
                    ])
                  }
                })
              }
              setNewCommentText('')
              setOpenComment && setOpenComment(false)
            }}
            paddingTop={1}
          >
            <TextField
              fullWidth
              value={newCommentText}
              onChange={(e) => setNewCommentText(e.target.value)}
              label="New Comment"
              minRows={2}
              multiline
            />
            <Button type="submit" variant="contained">
              <SendIcon />
            </Button>
          </Stack>
        )}
        {[...logs, ...logsToDisplay]
          .filter((x) => {
            const changes =
              selectedViews.includes('Changes Made') &&
              Object.keys(x.changesMade ?? {}).length > 0
            const recordAccess =
              selectedViews.includes('Access Logs') && x.recordAccess
            const notes =
              selectedViews.includes('Notes') && (x.note || x.comment)
            return changes || recordAccess || notes
          })
          .sort((a, b) => moment(b.createdAt) - moment(a.createdAt))
          .map((log) => (
            <SingleLog
              log={{
                ...log,
                author: log.author
                  ? log.author
                  : log.authorId
                    ? authors[log.authorId]
                    : null,
              }}
              key={log._id}
              handleDeleteComment={
                handleDeleteComment
                  ? () => handleDeleteComment(log._id)
                  : log.allowDelete || allowDelete
                    ? () => {
                        API.delete(`/logs?logId=${log._id}`).then((res) => {
                          if (res.status === 200) {
                            setLogsToDisplay((prev) =>
                              prev.filter((x) => x._id !== log._id),
                            )
                          }
                        })
                      }
                    : false
              }
            />
          ))}
        {!hideIfEmpty
          ? [...logs, ...logsToDisplay].filter((x) => {
              const changes =
                selectedViews.includes('Changes Made') &&
                Object.keys(x.changesMade ?? {}).length > 0
              const recordAccess =
                selectedViews.includes('Access Logs') && x.recordAccess
              const notes =
                selectedViews.includes('Notes') && (x.note || x.comment)
              return changes || recordAccess || notes
            }).length === 0 && (
              <Alert severity="info">
                No {title ? `${title}s` : 'logs'} to see.{' '}
                {[...logs, ...logsToDisplay].length > 0
                  ? 'Try adjusting the filters above.'
                  : ''}
              </Alert>
            )
          : ''}
        {fetchError && !loading && (
          <Alert severity="error">
            Unable to load. Please try again later.
          </Alert>
        )}
      </Stack>
    </Stack>
  )
}

export default LogsComponent
